// =====================================================
// SharersModal.tsx
// Affiche la liste des utilisateurs ayant partagé un post.
// Design identique à LikersModal — bottom sheet.
// =====================================================

import React, { useEffect, useState, useRef } from 'react';
import {
  Modal, View, Text, FlatList, Image,
  TouchableOpacity, ActivityIndicator,
  StyleSheet, Platform, Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';

const WS_BASE     = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const PROFILE_URL = `${WS_BASE}/profile`;
const REFRESH_URL = `${WS_BASE}/refresh-token`;

const { width: SCREEN_W } = Dimensions.get('window');

// ─── Types ────────────────────────────────────────────────────────────────────

interface Sharer {
  id:        string;
  user_id:   string;
  shared_at: string;
  profile: {
    nom:        string;
    prenom:     string;
    avatar_url: string | null;
  };
}

interface Props {
  visible: boolean;
  postId:  string | null;
  onClose: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getInitials = (nom: string, prenom: string) =>
  `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();

const formatTimeAgo = (dateString: string, t: (key: string, opts?: any) => string) => {
  const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (diff < 60)    return t('sharersModal.time.now');
  if (diff < 3600)  return t('sharersModal.time.minutes', { count: Math.floor(diff / 60) });
  if (diff < 86400) return t('sharersModal.time.hours', { count: Math.floor(diff / 3600) });
  return t('sharersModal.time.days', { count: Math.floor(diff / 86400) });
};

// ─── SafeImage ────────────────────────────────────────────────────────────────

function SafeImage({ uri, style, fallback }: {
  uri:      string | null | undefined;
  style:    any;
  fallback: React.ReactNode;
}) {
  const [error, setError] = useState(false);
  useEffect(() => { setError(false); }, [uri]);
  if (!uri || error) return <>{fallback}</>;
  return (
    <Image
      source={{ uri }}
      style={style}
      resizeMode="cover"
      onError={() => setError(true)}
    />
  );
}

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────────

export default function SharersModal({ visible, postId, onClose }: Props) {
  const { t } = useTranslation();
  const [sharers, setSharers] = useState<Sharer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const accessTokenRef  = useRef('');
  const refreshTokenRef = useRef('');
  const expiresAtRef    = useRef(0);

  // ─── Token ──────────────────────────────────────────────────────────────────

  const getValidToken = async (): Promise<string> => {
    if (!accessTokenRef.current) {
      const raw = await AsyncStorage.getItem('harmonia_session');
      if (!raw) return '';
      const session           = JSON.parse(raw);
      accessTokenRef.current  = session.access_token  || '';
      refreshTokenRef.current = session.refresh_token || '';
      expiresAtRef.current    = session.expires_at    || 0;
    }
    const now = Math.floor(Date.now() / 1000);
    if (expiresAtRef.current - now > 60) return accessTokenRef.current;
    try {
      const res  = await fetch(REFRESH_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ refresh_token: refreshTokenRef.current }),
      });
      const data = await res.json();
      if (!res.ok) return '';
      accessTokenRef.current  = data.access_token;
      refreshTokenRef.current = data.refresh_token;
      expiresAtRef.current    = data.expires_at;
      const raw = await AsyncStorage.getItem('harmonia_session');
      if (raw) await AsyncStorage.setItem('harmonia_session', JSON.stringify({
        ...JSON.parse(raw),
        access_token:  data.access_token,
        refresh_token: data.refresh_token,
        expires_at:    data.expires_at,
      }));
      return data.access_token;
    } catch { return ''; }
  };

  // ─── Chargement ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (visible && postId) {
      loadSharers();
    } else {
      setSharers([]);
      setError(null);
    }
  }, [visible, postId]);

  const loadSharers = async () => {
    if (!postId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) { setError(t('sharersModal.errors.sessionExpired')); return; }
      const res  = await fetch(PROFILE_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          action:       'get-sharers',
          access_token: token,
          post_id:      postId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSharers(data.sharers || []);
    } catch (err: any) {
      setError(err.message || t('sharersModal.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // ─── Render item ─────────────────────────────────────────────────────────────

  const renderSharer = ({ item }: { item: Sharer }) => (
    <View style={styles.sharerRow}>
      <SafeImage
        uri={item.profile.avatar_url}
        style={styles.avatar}
        fallback={
          <View style={[styles.avatar, styles.avatarPlaceholder]}>
            <Text style={styles.avatarText}>
              {getInitials(item.profile.nom, item.profile.prenom)}
            </Text>
          </View>
        }
      />
      <View style={styles.sharerInfo}>
        <Text style={styles.sharerName}>
          {item.profile.prenom} {item.profile.nom}
        </Text>
        <View style={styles.sharedAtRow}>
          <Ionicons name="repeat-outline" size={12} color="#999" />
          <Text style={styles.sharedAt}>
            {t('sharersModal.sharedAt', { time: formatTimeAgo(item.shared_at, t) })}
          </Text>
        </View>
      </View>
    </View>
  );

  // ─── Rendu ───────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.overlay}>

        {/* Backdrop cliquable pour fermer */}
        <TouchableOpacity
          style={StyleSheet.absoluteFillObject}
          activeOpacity={1}
          onPress={onClose}
        />

        <View style={styles.sheet}>

          {/* Handle */}
          <View style={styles.handle} />

          {/* Header */}
          <View style={styles.header}>
            <Ionicons name="repeat-outline" size={20} color="#7B1FA2" />
            <Text style={styles.headerTitle}>
              {sharers.length > 0
                ? t('sharersModal.header.titleWithCount', { count: sharers.length })
                : t('sharersModal.header.title')}
            </Text>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
              <Ionicons name="close" size={22} color="#666" />
            </TouchableOpacity>
          </View>

          {/* Contenu */}
          {loading ? (
            <View style={styles.centered}>
              <ActivityIndicator size="large" color="#7B1FA2" />
              <Text style={styles.loadingText}>{t('sharersModal.loading')}</Text>
            </View>
          ) : error ? (
            <View style={styles.centered}>
              <Ionicons name="alert-circle-outline" size={48} color="#EF4444" />
              <Text style={styles.errorText}>{error}</Text>
              <TouchableOpacity style={styles.retryBtn} onPress={loadSharers}>
                <Text style={styles.retryBtnText}>{t('sharersModal.retry')}</Text>
              </TouchableOpacity>
            </View>
          ) : sharers.length === 0 ? (
            <View style={styles.centered}>
              <Ionicons name="repeat-outline" size={56} color="#DDD" />
              <Text style={styles.emptyText}>{t('sharersModal.empty')}</Text>
            </View>
          ) : (
            <FlatList
              data={sharers}
              keyExtractor={item => item.id}
              renderItem={renderSharer}
              contentContainerStyle={styles.list}
              showsVerticalScrollIndicator={false}
              ItemSeparatorComponent={() => <View style={styles.separator} />}
            />
          )}

        </View>
      </View>
    </Modal>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  overlay: {
    flex:            1,
    justifyContent:  'flex-end',
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  sheet: {
    backgroundColor:     '#fff',
    borderTopLeftRadius:  24,
    borderTopRightRadius: 24,
    maxHeight:            '75%',
    paddingBottom:        Platform.OS === 'ios' ? 36 : 20,
  },
  handle: {
    width:           40,
    height:          4,
    backgroundColor: '#E0E0E0',
    borderRadius:    2,
    alignSelf:       'center',
    marginTop:       12,
    marginBottom:    4,
  },
  header: {
    flexDirection:     'row',
    alignItems:        'center',
    paddingHorizontal: 20,
    paddingVertical:   14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#EBEBEB',
    gap:               8,
  },
  headerTitle: {
    flex:       1,
    fontSize:   17,
    fontWeight: '700',
    color:      '#1A1A1A',
  },
  closeBtn: {
    padding: 4,
  },
  list: {
    paddingHorizontal: 20,
    paddingTop:        8,
    paddingBottom:     16,
  },
  sharerRow: {
    flexDirection:   'row',
    alignItems:      'center',
    paddingVertical: 12,
    gap:             12,
  },
  separator: {
    height:          StyleSheet.hairlineWidth,
    backgroundColor: '#F0F0F0',
  },
  avatar: {
    width:        46,
    height:       46,
    borderRadius: 23,
  },
  avatarPlaceholder: {
    backgroundColor: '#8A2BE2',
    justifyContent:  'center',
    alignItems:      'center',
  },
  avatarText: {
    color:      '#fff',
    fontSize:   15,
    fontWeight: 'bold',
  },
  sharerInfo: {
    flex: 1,
    gap:  3,
  },
  sharerName: {
    fontSize:   15,
    fontWeight: '600',
    color:      '#1A1A1A',
  },
  sharedAtRow: {
    flexDirection: 'row',
    alignItems:    'center',
    gap:           4,
  },
  sharedAt: {
    fontSize: 12,
    color:    '#999',
  },
  centered: {
    paddingVertical: 48,
    alignItems:      'center',
    gap:             12,
  },
  loadingText: {
    fontSize: 14,
    color:    '#666',
  },
  errorText: {
    fontSize:          14,
    color:             '#EF4444',
    textAlign:         'center',
    paddingHorizontal: 24,
  },
  retryBtn: {
    paddingHorizontal: 24,
    paddingVertical:   10,
    backgroundColor:   '#7B1FA2',
    borderRadius:      10,
    marginTop:         4,
  },
  retryBtnText: {
    color:      '#fff',
    fontSize:   14,
    fontWeight: '600',
  },
  emptyText: {
    fontSize:  15,
    color:     '#999',
    textAlign: 'center',
  },
});
